import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { ColorKeys, Product, TextOverrides } from '../../../../../../types/working-model';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import SortableProduct from './product';
import CreateProductModal from '../../../../content-creation/products';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import Controls from '../../../session/editor/moduleControls/controls';
import classNames from 'classnames';
import { getStylingOverrides } from '../../../../../live-event/utils';
import '../../../../../live-event/modules/products/products.scss';
import './products.scss';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import SelectNative from '../../../../../general-ui/select/select-native';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import LargeButton from '../../../../../general-ui/button/large-button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicLangingModuleProps } from '../editor-main';

export interface VisibleProduct extends Product {
	selected?: boolean;
}

const EditorProducts: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const dispatch = useDispatch();

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);
	const [modalOpen, setModalOpen] = useState(false);
	const [productToEdit, setProductToEdit] = useState<null | Product>(null);
	const [replacing, setReplacing] = useState<Product | null>(null);

	const { t } = useTranslation("homepage");

	const content = page_module?.content;
	const layout_type = page_module?.content?.layout_type || "standard";
	const styling_overrides = page_module?.styling_overrides;
	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							language,
							baseLanguage
						}),
					},
				})
			);
		};
	}
	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const displayEditModal = useCallback((product: Product) => {
		setProductToEdit(product);
		setModalOpen(true);
	}, []);

	const replaceProduct = useCallback((product: Product) => {
		setProductToEdit(null);
		setReplacing(product);
		setModalOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setModalOpen(false);
		setReplacing(null);
	}, []);

	const openModal = useCallback(() => {
		setReplacing(null);
		setProductToEdit(null);
		setModalOpen(true);
	}, []);

	function handleDragEnd(event: any) {
		const _modules = [...page_module.modules!];

		const startIndex = _modules.findIndex((product: Product) => Number(product.product) === Number(event.active.id));
		const newIndex = _modules.findIndex((product: Product) => Number(product.product) === Number(event.over.id));

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updateHomepageModule(
				{
					...page_module,
					modules: _modules,
					content_modules: _modules.map((product: Product) => product.product)
				}
			)
		);
	}

	function onSelectionComplete(products: Product[]) {
		dispatch(
			updateHomepageModule(
				{
					...page_module,
					modules: products,
					content_modules: products.map((product) => product.product!)
				}
			)
		);
	}

	function removeProduct(productToRemove: Product) {
		if (page_module.modules) {
			const filteredModules = page_module.modules.filter((module: Product) => module.product !== productToRemove.product);

			dispatch(
				updateHomepageModule({
					...page_module,
					modules: filteredModules,
					content_modules: filteredModules.map(product => product.product!)
				})
			);
		}
	}

	const { custom_heading, description, title } = getTextValue(content, language);

	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	return page_module.is_on ? (
		<div
			ref={ref}
			className={classNames(
				"editor-wrapper products",
				`products-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				openContentModal={openModal}
				module={page_module}
				contentButtonText="Add Products"
				editableContent={{
					jsx: <p>placeholder</p>,
					onDone: () => ('placeholder')
				}}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="products-text-wrapper">
					<EditableTextSlim
						tagType="p"
						className="products-custom-heading"
						value={custom_heading}
						defaultColor={customHeadingDefaultColor}
						onChange={handleTextChange('custom_heading')}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
						elementName={EContentColorOverrideNames.custom_heading}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
					/>

					<div className="products-top">
						<div className="text-container">
							<EditableTextSlim
								tagType="h2"
								className="products-title"
								value={title}
								defaultColor={ColorKeys.bodyTextColor}
								onChange={handleTextChange('title')}
								onOverrideChange={handleStyleOverrideChange('title')}
								elementName={EContentColorOverrideNames.title}
								colorOverride={styling_overrides?.content?.title?.color}
							/>
							<EditableTextDraft
								className="products-description"
								value={description}
								onChange={handleTextChange('description')}
								defaultColor={ColorKeys.bodyTextColor}
								onOverrideChange={handleStyleOverrideChange('description')}
								elementName={EContentColorOverrideNames.description}
								colorOverride={styling_overrides?.content?.description?.color}
							/>
						</div>
						<div className="dropdown-container">
							<SelectNative
								className="products-dropdown"
								options={[{ label: t('All attributes'), value: '' }]}
							/>
							<SelectNative
								className="products-dropdown"
								options={[{ label: t('All categories'), value: '' }]}
							/>
						</div>
					</div>
				</div>
				{page_module.modules && page_module.modules.length > 0 ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={page_module.modules!.map((module: Product) => String(module.product))}
							strategy={rectSortingStrategy}
						>
							<ul className="product-list">
								{page_module.modules.map((module: Product) => (
									<SortableProduct
										product={module}
										key={module.product}
										id={String(module.product)}
										editProduct={() => displayEditModal(module)}
										removeProduct={() => removeProduct(module)}
										replaceProduct={() => replaceProduct(module)}
										template={template}
										language={language}
									/>
								))}
							</ul>
						</SortableContext>
					</DndContext>
				)
					: <LargeButton
						title="Add Product"
						onClick={openModal}
						style={{ width: "100%", marginTop: '20px' }}
					/>}

			</div>

			<CreateProductModal
				baseLanguage={baseLanguage}
				language={language}
				title={"Add products"}
				open={modalOpen}
				productToEdit={productToEdit}
				close={closeModal}
				onComplete={onSelectionComplete}
				initialSelected={page_module.modules ?? []}
				replacing={replacing}
			/>
		</div>
	) : null;
});

EditorProducts.displayName = 'EditorProducts';

export default EditorProducts;